import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  Grid,
  TextField,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import axios from "../../../config/axiosConfig";
import PageHeader from "../../../components/layout/PageHeader";

// Servicios que quedan fuera del módulo de OT desde la búsqueda de cliente
// (AVL/Scada no usan ot_solicitud en este flujo).
const SERVICIOS_SIN_ADICION = ["AVL", "Scada"];

// id de ot_tipo "Devolucion" — igual que OrdenTrabajoPaso.jsx. Esta página duplica su
// propia copia de los "puede*" (para tienePantallaDeTrabajo/EstadoBar), así que necesita
// el mismo criterio para no tratar una OT de Devolución como si fuera de Adición.
const TIPO_OT_DEVOLUCION = 5;
// id de ot_tipo "Venta de Equipo" — usado solo para elegir los estados correctos al
// armar el detalle del paso "Asignado" (ver cargarEquiposAsignados): Venta usa sus
// propios estados (Vendido/Vendido Activo, Activo para Chip), distintos de los que usan
// Adición/Reemplazo/Mantenimiento (Asignado/Activo).
const TIPO_OT_VENTA = 11;
// id de ot_tipo "Venta de Equipos Activos" — variante de Venta sin el estado
// intermedio "Vendido" (ver puedeVenderActivo); igual que Venta, incluye AVL/Scada.
const TIPO_OT_VENTA_ACTIVOS = 14;
// id de ot_tipo "Activar Equipos Vendidos" — mismo criterio que Venta pero con sus
// propios estados (Asignado Vendido -> Vendido Activo/Activo, ver ESTADOS_EQUIPO_ACTIVAR_VENDIDOS).
const TIPO_OT_ACTIVAR_VENDIDOS = 12;
// id de ot_tipo "Desactivar Equipos Vendidos" — inversa de Activar: un solo paso en
// estado 1-2 (ver DesactivarVendidosOT.jsx), sin pasar por Asignado/Procesando/Entregado.
const TIPO_OT_DESACTIVAR_VENDIDOS = 13;
// id de ot_tipo "Adicion de Chips" (solo AVL/Scada): un solo paso en estado 1-2 (ver AdicionChipsOT.jsx).
const TIPO_OT_ADICION_CHIPS = 28;
// id de ot_tipo "Devolucion de Chips" (solo AVL/Scada): un solo paso en estado 1-2 (ver DevolucionChipsOT.jsx).
const TIPO_OT_DEVOLUCION_CHIPS = 29;
// id de ot_tipo "Mantenimiento Traspaso" — creación exclusiva de Cochabamba, pero el
// diagnóstico lo continúa un usuario de La Paz una vez que el equipo llega
// físicamente (ver bloqueadoPorTraspasoCochabamba más abajo). También se usa para el
// botón de reimprimir la Nota de Traspaso, disponible para cualquier sucursal desde
// que se envía el equipo (estado 3).
const TIPO_OT_MANTENIMIENTO_TRASPASO = 8;
// id de ot_tipo "Mantenimiento" — a diferencia de Adición/Reemplazo/Venta, su Nota
// (otNotaMantenimientoPdfController.js) no es la genérica "Nota de Entrega" (esa trae
// título/columnas de Adición, "... ADICIONADAS"), así que necesita su propio botón.
const TIPO_OT_MANTENIMIENTO = 2;
// "Mantenimiento Equipos Vendidos": misma Nota de Mantenimiento que el tipo normal.
const TIPO_OT_MANTENIMIENTO_VENDIDOS = 18;
// id de ot_tipo "Remplazo Equipos Vendidos Activos" — mismo motivo que Mantenimiento:
// su Nota propia (otNotaRemplazoVendidosActivosPdfController.js) muestra precio_venta y
// el estado 'Vendido Activo', que la genérica "Nota de Entrega" no contempla.
const TIPO_OT_REMPLAZO_VENDIDOS_ACTIVOS = 16;
// id de ot_tipo "Reemplazo" — solo para saber que su paso Asignar (estado 3) también tiene pantalla propia.
const TIPO_OT_REEMPLAZO = 4;

const ESTADOS = ["Eliminado", "Emitido", "Detalle", "Asignado", "Procesando", "Entregado", "Concluido", "Finalizado"];

const fmt = (value) => (value && String(value).trim() ? value : "—");

const OBSERVACION_LABEL = {
  5: "Observación de entrega",
  6: "Observación de conclusión",
};

const colorEventoHistorial = (evento = "") => {
  const e = evento.toLowerCase();
  if (e.includes("finalizado")) return "success";
  if (e.includes("concluido")) return "info";
  if (e.includes("entregado")) return "primary";
  if (e.includes("registro")) return "secondary";
  return "grey";
};

// Cada segmento de `detalle` es "Label : valor" o "Label: valor", separados por "%%%".
const parseDetalle = (detalle) =>
  String(detalle || "")
    .split("%%%")
    .map((seg) => {
      const idx = seg.indexOf(":");
      if (idx === -1) return null;
      return { label: seg.slice(0, idx).trim(), value: seg.slice(idx + 1).trim() };
    })
    .filter(Boolean);

// Un evento de historial "pertenece" a un paso si lo dejó en ese estado
// (evento="Cambio de Estado a X", o "Avance de estado" genérico con "estado nuevo:
// X" en el detalle). "Emitido" no pasa por acá (ver abrirDetallePaso, lee directo de
// ot_solicitud, igual que cargaDetalle.php). "Detalle" no tiene forma de
// detectarse: guarda.php pasa de Emitido a Detalle con un PUT silencioso, sin
// historial (ver handleEquiposActualizados en OrdenTrabajoPaso.jsx) — para ese
// paso simplemente no habrá filas.
const matchesPaso = (h, pasoLabel) => {
  if (h.evento === `Cambio de Estado a ${pasoLabel}`) return true;
  if (h.evento === "Avance de estado" && String(h.detalle || "").includes(`estado nuevo: ${pasoLabel}`)) return true;
  return false;
};

// Campos "extra" a mostrar por paso, además de Usuario/Fecha (siempre presentes). Se
// excluyen los campos redundantes con el propio título del paso (Cod ot, estado anterior/
// nuevo/Estado) automáticamente en buildFilasPaso.
const CAMPOS_OCULTOS = ["cod ot", "estado anterior", "estado nuevo", "estado"];

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
      <Typography variant="body2">
        <b>{label}:</b> {value}
      </Typography>
    </Stack>
  );
}

// Conector de líneas entre círculos: verde cuando el paso siguiente ya está
// completado o activo, gris cuando todavía no se llegó ahí.
const EstadoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 12 },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.success.main,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.success.main,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    borderColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300],
  },
}));

const EstadoIconRoot = styled("div")(({ theme, ownerState }) => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: 12,
  fontWeight: 700,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300],
  cursor: ownerState.clicable ? "pointer" : "default",
  ...(ownerState.completado && { backgroundColor: theme.palette.success.main }),
  ...(ownerState.activo && {
    backgroundColor: theme.palette.info.main,
    boxShadow: `0 0 0 4px ${alpha(theme.palette.info.main, 0.18)}`,
  }),
}));

function EstadoIcon({ completado, activo, clicable, onClick, numero }) {
  return (
    <EstadoIconRoot ownerState={{ completado, activo, clicable }} onClick={onClick}>
      {completado ? <CheckIcon sx={{ fontSize: 15 }} /> : numero}
    </EstadoIconRoot>
  );
}

// Igual que la barra de estados de escritorio_detalle.php, con el estilo de stepper
// circular pedido: los estados ya pasados en verde con check, el siguiente resaltado en
// azul, y los que faltan en gris. Regla fija: SOLO el paso azul (siguiente) navega — si
// tiene una pantalla de trabajo propia (Adición, Emparejar, Entrega, Concluir, Finalizar,
// Fase 2 de Devolución) navega ahí; si no, abre el diálogo genérico de avanzar
// directamente acá. El azul siempre lleva al lugar correcto sin importar qué número le
// tocó: handleClicSiguiente lee tienePantallaDeTrabajo del estado REAL de la OT, no del
// nodo que se clickeó. Los pasos verdes (completados, incluido el que coincide con el
// estado actual) NUNCA navegan — solo abren el historial de la OT (no hay forma de
// filtrarlo por paso puntual, así que se muestra completo).
function EstadoBar({ estado, onClicSiguiente, onClicCompletado, bloqueado }) {
  const pasos = ESTADOS.slice(1); // 1..7, "Eliminado" no entra en la barra

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", p: 2 }}>
      <Box sx={{ textAlign: "center", mb: 2, fontWeight: 700, fontSize: 13 }}>
        Estado del Proceso Orden de Trabajo
      </Box>
      <Stepper alternativeLabel connector={<EstadoConnector />}>
        {pasos.map((label, idx) => {
          const i = idx + 1;
          // `bloqueado` (Mantenimiento Traspaso visto desde Cochabamba, ver
          // bloqueadoPorTraspasoCochabamba): esta OT no avanza más para este usuario, así
          // que ningún paso posterior al actual se marca "siguiente" — sin azul, sin click,
          // sin diálogo de avanzar genérico.
          const esSiguiente = !bloqueado && estado > 0 && i === estado + 1;
          const esCompletado = estado === 0 || i <= estado;
          const esClicable = esSiguiente || esCompletado;
          const onClick = esSiguiente ? onClicSiguiente : esCompletado ? () => onClicCompletado(label) : undefined;
          const icono = (
            <EstadoIcon
              numero={i}
              completado={esCompletado}
              activo={esSiguiente}
              clicable={esClicable}
              onClick={onClick}
            />
          );
          return (
            <Step key={label}>
              <StepLabel StepIconComponent={() => icono}>
                {esClicable ? (
                  <Tooltip title={esSiguiente ? `Ir a "${label}"` : `Ver detalle de "${label}"`}>
                    <Box component="span" sx={{ cursor: "pointer" }} onClick={onClick}>
                      {label}
                    </Box>
                  </Tooltip>
                ) : (
                  label
                )}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Paper>
  );
}

// idProp/embedded: permite renderizar esta misma pantalla dentro de un Dialog
// (ej. "Detalle de OT" de Historial de Cliente) en vez de como página propia con
// su URL/ruta — mismo patrón que RequerimientoDetalle (codProp/embedded). Sin
// idProp usa el :id de la ruta como siempre. `embedded` oculta el layout de
// dashboard (ya está dentro de otra pantalla) y la flecha de "volver" del
// PageHeader (usa el botón de cerrar del Dialog en su lugar).
const OrdenTrabajoDetalle = ({ idProp, embedded = false } = {}) => {
  const { id: idFromParams } = useParams();
  const id = idProp ?? idFromParams;
  const navigate = useNavigate();
  const { user } = useAuth();
  const Layout = embedded ? React.Fragment : DashboardLayout;

  const [orden, setOrden] = useState(null);
  const [loading, setLoading] = useState(true);

  const [avanzarOpen, setAvanzarOpen] = useState(false);
  const [avanzarObs, setAvanzarObs] = useState("");
  const [avanzando, setAvanzando] = useState(false);
  const [avanzarError, setAvanzarError] = useState("");
  // Candado sincrónico contra doble click/doble submit (no depende del re-render que
  // aplica el `disabled` del botón) — se vio un caso real duplicando historial.
  const enviandoEstadoRef = useRef(false);

  const [historialOpen, setHistorialOpen] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [errorHistorial, setErrorHistorial] = useState("");

  // Botón "Editar Estado" — solo admin (ver ESTADOS/permitido más abajo). Permite
  // saltar a cualquier estado, a diferencia de "Avanzar" que solo suma un paso.
  const [editarEstadoOpen, setEditarEstadoOpen] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState(0);
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const [errorEditarEstado, setErrorEditarEstado] = useState("");

  const [pasoDialog, setPasoDialog] = useState(null); // { label } | null
  const [pasoFilas, setPasoFilas] = useState([]);
  const [pasoColumnas, setPasoColumnas] = useState([]);
  const [cargandoPaso, setCargandoPaso] = useState(false);
  const [errorPaso, setErrorPaso] = useState("");
  // Click en la columna "Serie" de la tabla de "Asignado"/"Procesando" abre el detalle
  // de Centro de Equipos para ese equipo puntual (mismo diálogo que ya usan las
  // pantallas de selección de equipo, ver CentroEquipoDetalleDialog).
  const [equipoDialog, setEquipoDialog] = useState(null); // { tipo, identificador } | null

  const [imprimiendo, setImprimiendo] = useState(false);

  useEffect(() => {
    axios.get(`/api/ot-solicitudes/${id}`)
      .then((res) => {
        setOrden(res.data);
      })
      .catch((err) => {
        console.error("Error al cargar la orden", err);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const abrirHistorial = () => {
    setHistorialOpen(true);
    setCargandoHistorial(true);
    setErrorHistorial("");
    axios.get(`/api/ot-solicitudes/${id}/historial`)
      .then((res) => setHistorial(Array.isArray(res.data) ? res.data : []))
      .catch((err) => setErrorHistorial(err?.response?.data?.error || "No se pudo cargar el historial."))
      .finally(() => setCargandoHistorial(false));
  };

  // "Asignado" no sale del log de historial_ot (que solo tiene metadatos del cambio de
  // estado) sino de los equipos realmente asignados a la OT — mismo criterio que ya usa
  // ConcluirOT.jsx / la Nota de Entrega: TKR -> Radio+Batería, el resto -> Varios+Chip,
  // aceptando estado "Asignado" (todavía no concluida) o "Activo" (ya concluida). Venta
  // usa sus PROPIOS estados (Vendido/Vendido Activo, Activo para Chip) — sin esto, el
  // paso "Asignado" de una OT de Venta siempre salía vacío (los estados nunca coinciden).
  const ESTADOS_EQUIPO_OT = ["Asignado", "Activo"];
  const ESTADOS_EQUIPO_VENTA = ["Vendido", "Vendido Activo", "Activo"];
  const ESTADOS_EQUIPO_ACTIVAR_VENDIDOS = ["Asignado Vendido", "Vendido Activo", "Activo"];
  // Para el click en "Serie" de la tabla de Asignado/Procesando: ahí el valor de "Tipo"
  // puede ser el label genérico (Radio/Batería/Chip/Módem) o, para "otros" servicios, el
  // tipo específico de equipo_varios (p.ej. "Radio POC") — cualquier otro string distinto
  // de esos labels conocidos es, por descarte, un equipo Varios.
  const familiaDeTipoLabel = (tipoLabel) => {
    const t = String(tipoLabel || "").trim().toLowerCase();
    if (t === "chip") return "chip";
    if (t === "radio") return "radio";
    if (t === "batería" || t === "bateria") return "bateria";
    if (t === "módem" || t === "modem") return "modem";
    return "varios";
  };

  const cargarEquiposAsignados = async () => {
    const esVentaOrden = Number(orden?.tipo_ot) === TIPO_OT_VENTA;
    // Venta de Equipos Activos (14) deja los equipos en los MISMOS estados que Venta
    // (Vendido/Vendido Activo) — sin incluirla acá, una OT de este tipo caía en la rama
    // genérica ESTADOS_EQUIPO_OT (Asignado/Activo, que nunca matchea) y además, si el
    // servicio era AVL, ni siquiera consultaba historial-modem (ver el if de abajo) —
    // el paso "Asignado" salía vacío pese a tener equipos (ver OT #42121).
    const esVentaActivosOrden = Number(orden?.tipo_ot) === TIPO_OT_VENTA_ACTIVOS;
    const esActivarVendidosOrden = Number(orden?.tipo_ot) === TIPO_OT_ACTIVAR_VENDIDOS;
    const esDevolucionOrden = Number(orden?.tipo_ot) === TIPO_OT_DEVOLUCION;
    const esMantenimientoVendidosOrden = Number(orden?.tipo_ot) === TIPO_OT_MANTENIMIENTO_VENDIDOS;
    const esTKROrden = orden?.tipo_servicio === "TKR";
    const esAVLOrden = orden?.tipo_servicio === "AVL" || orden?.tipo_servicio === "Scada";
    // Devolución: el equipo puede terminar en cualquier estado final (Espera en Fase 1,
    // Inactivo/Mantenimiento/Baja/Robado tras Fase 2 o una transferencia de cliente) —
    // igual que hacía el PHP viejo (cargaDetalle.php), acá NO se filtra por estado, se
    // muestra todo lo que está bajo este cod_ot tal cual (la consulta ya viene acotada
    // por cod_ot, no hace falta filtrar de nuevo).
    const estadosOk = esVentaOrden || esVentaActivosOrden
      ? ESTADOS_EQUIPO_VENTA
      : esActivarVendidosOrden
      ? ESTADOS_EQUIPO_ACTIVAR_VENDIDOS
      : ESTADOS_EQUIPO_OT;
    // Devolución y Mantenimiento Equipos Vendidos muestran todo lo que está bajo la OT: el equipo puede estar en Activo, Vendido Activo,
    // Mantenimiento o reemplazado (el historial ya viene acotado por cod_ot).
    const pasaFiltro = (estadoFila) => esDevolucionOrden || esMantenimientoVendidosOrden || estadosOk.includes(estadoFila);

    // Devolución de Chips: solo chips, que quedan Inactivos (sin filtro de estado): se muestran los movimientos 'Devolucion' bajo esta OT.
    if (Number(orden?.tipo_ot) === TIPO_OT_DEVOLUCION_CHIPS) {
      const { data } = await axios.get("/api/historial-chip", { params: { cod_ot: id } });
      return (data || []).filter((r) => r.operacion === "Devolucion").map((r) => ({
        Serie: r.numero, Tipo: "Chip", Modelo: r.modelo, Ciudad: r.ciudad_actual || r.ciudad, Observación: r.observacion, Usuario: r.usu,
      }));
    }

    if ((esVentaOrden || esVentaActivosOrden || esActivarVendidosOrden) && esAVLOrden) {
      const [resModem, resChip] = await Promise.all([
        axios.get("/api/historial-modem", { params: { cod_ot: id } }),
        axios.get("/api/historial-chip", { params: { cod_ot: id } }),
      ]);
      // Módem sí recibe Placa/ID Cliente al activarse (activarVendidoTx); Chip no.
      return [
        ...(resModem.data || []).filter((r) => pasaFiltro(r.estado)).map((r) => ({
          Serie: r.num_sim, Tipo: "Módem", Modelo: r.modelo, Ciudad: r.ciudad_actual || r.ciudad,
          "ID Cliente": r.id_ref, Placa: r.placa, Precio: r.precio_venta, Observación: r.observacion, Usuario: r.usu,
        })),
        ...(resChip.data || []).filter((r) => pasaFiltro(r.estado)).map((r) => ({
          Serie: r.numero, Tipo: "Chip", Modelo: r.modelo, Ciudad: r.ciudad_actual || r.ciudad,
          Precio: r.precio_venta, Observación: r.observacion, Usuario: r.usu,
        })),
      ];
    }

    const peticiones = esTKROrden
      ? [axios.get("/api/historial-radio", { params: { cod_ot: id } }), axios.get("/api/historial-bateria", { params: { cod_ot: id } })]
      : [axios.get("/api/historial-varios", { params: { cod_ot: id } }), axios.get("/api/historial-chip", { params: { cod_ot: id } })];
    const [res1, res2] = await Promise.all(peticiones);

    // Grupo/ID Cliente/Placa solo existen para Radio y Varios (no-Chip); Baterías y Chips
    // solo guardan Observación — mismos campos que se llenan al tildar el check en
    // Asignar Equipos (ver captura: Grupo/ID Cliente/Placa se habilitan ahí mismo). Venta
    // ADEMÁS agrega Precio (precio_venta) — no en lugar de Grupo/ID Cliente/Placa, la
    // Radio vendida y activada recibe ambos (activarVendidoTx los llena igual que
    // Adición/Reemplazo).
    if (esTKROrden) {
      return [
        ...(res1.data || []).filter((r) => pasaFiltro(r.estado)).map((r) => ({
          Serie: r.serie, Tipo: "Radio", Modelo: r.modelo, Ciudad: r.ciudad_actual || r.ciudad,
          Grupo: r.grupo, "ID Cliente": r.id_ref, Placa: r.placa,
          ...(esVentaOrden || esVentaActivosOrden || esActivarVendidosOrden ? { Precio: r.precio_venta } : {}),
          Observación: r.observacion, Usuario: r.usu,
        })),
        ...(res2.data || []).filter((r) => pasaFiltro(r.estado)).map((r) => ({
          Serie: r.serie, Tipo: "Batería", Modelo: r.modelo, Ciudad: r.ciudad_actual || r.ciudad,
          ...(esVentaOrden || esVentaActivosOrden || esActivarVendidosOrden ? { Precio: r.precio_venta } : {}),
          Observación: r.observacion, Usuario: r.usu,
        })),
      ];
    }
    return [
      ...(res1.data || []).filter((r) => pasaFiltro(r.estado)).map((r) => ({
        Serie: r.serie, Tipo: r.tipo, Modelo: r.modelo, Ciudad: r.ciudad_actual || r.ciudad,
        Grupo: r.grupo, "ID Cliente": r.id_ref, Placa: r.placa, Observación: r.observacion, Usuario: r.usu,
      })),
      ...(res2.data || []).filter((r) => pasaFiltro(r.estado)).map((r) => ({
        Serie: r.numero, Tipo: "Chip", Modelo: r.modelo, Ciudad: r.ciudad_actual || r.ciudad,
        Grupo: undefined, "ID Cliente": undefined, Placa: undefined, Observación: r.observacion, Usuario: r.usu,
      })),
    ];
  };

  // Click en un paso completado de la barra: a diferencia de "Ver Historial" (todo el
  // log), acá se filtra solo lo que corresponde a ESE paso puntual. "Emitido" tiene un
  // armado curado (Servicio/Observaciones/Usuario, pedido explícitamente); "Asignado"
  // muestra los equipos realmente asignados a la OT; el resto arma las columnas
  // dinámicamente a partir de los campos que trae el `detalle` de ese evento.
  const abrirDetallePaso = (label) => {
    setPasoDialog({ label });
    setCargandoPaso(true);
    setErrorPaso("");

    // "Emitido" nunca dependió del historial en el sistema viejo (cargaDetalle.php):
    // lee directo de ot_solicitud (tipo_servicio/observacion/id_usu) — por eso
    // funciona igual sin importar cómo se haya creado la OT (ver OT #42943, creada
    // por un camino legacy que nunca dejó rastro en historial_ot).
    if (label === "Emitido") {
      setPasoColumnas(["Servicio", "Observaciones", "Usuario"]);
      setPasoFilas([{
        Servicio: orden?.tipo_servicio || "—",
        Observaciones: orden?.observacion || "—",
        Usuario: orden?.id_usu || "—",
      }]);
      setCargandoPaso(false);
      return;
    }

    // "Detalle" — ítems/precios cargados para esta OT (ot_detalle), igual que
    // cargaDetalle.php. La mayoría de los tipos de OT nunca escriben acá.
    if (label === "Detalle") {
      axios.get(`/api/ot-solicitudes/${id}/detalle`)
        .then((res) => {
          const items = Array.isArray(res.data) ? res.data : [];
          setPasoColumnas(["Item", "Detalle", "Cantidad", "Precio Unitario", "Total"]);
          setPasoFilas(items.map((it) => ({
            Item: it.item,
            Detalle: it.detalle,
            Cantidad: it.cantidad,
            "Precio Unitario": it.precio_unitario,
            Total: it.total,
          })));
        })
        .catch((err) => setErrorPaso(err?.response?.data?.error || "No se pudo cargar el detalle de este paso."))
        .finally(() => setCargandoPaso(false));
      return;
    }

    if (label === "Asignado" || label === "Procesando") {
      const esVentaOrden = Number(orden?.tipo_ot) === TIPO_OT_VENTA;
      const esVentaActivosOrden = Number(orden?.tipo_ot) === TIPO_OT_VENTA_ACTIVOS;
      const esActivarVendidosOrden = Number(orden?.tipo_ot) === TIPO_OT_ACTIVAR_VENDIDOS;
      const conPrecio = esVentaOrden || esVentaActivosOrden || esActivarVendidosOrden;
      cargarEquiposAsignados()
        .then((filas) => {
          // "Asignado": vista simple (qué se asignó y quién). "Procesando": los mismos
          // equipos pero con los campos que se llenan al asignarlos (Grupo/ID Cliente/
          // Placa/Observación), pedido explícitamente. Venta y Activar Equipos Vendidos
          // agregan Precio de venta a lo mismo (Grupo/ID Cliente/Placa se llenan igual al
          // activar el equipo vendido).
          setPasoColumnas(
            label === "Asignado"
              ? ["Serie", "Tipo", "Modelo", "Ciudad", ...(conPrecio ? ["Precio"] : []), "Usuario"]
              : ["Serie", "Tipo", "Modelo", "Grupo", "ID Cliente", "Placa", ...(conPrecio ? ["Precio"] : []), "Observación", "Usuario"]
          );
          setPasoFilas(filas);
        })
        .catch((err) => setErrorPaso(err?.response?.data?.error || "No se pudieron cargar los equipos asignados."))
        .finally(() => setCargandoPaso(false));
      return;
    }

    axios.get(`/api/ot-solicitudes/${id}/historial`)
      .then((res) => {
        const todos = Array.isArray(res.data) ? res.data : [];
        const coincidencias = todos.filter((h) => matchesPaso(h, label));

        const filas = coincidencias.map((h) => {
          const campos = {};
          parseDetalle(h.detalle)
            .filter((f) => !CAMPOS_OCULTOS.includes(f.label.toLowerCase()))
            .forEach((f) => { campos[f.label] = f.value; });
          campos.Usuario = h.usu || "—";
          campos.Fecha = `${fmt(h.fecha && String(h.fecha).slice(0, 10))} ${h.hora || ""}`.trim();
          return campos;
        });
        setPasoColumnas([...new Set(filas.flatMap((f) => Object.keys(f)))]);
        setPasoFilas(filas);
      })
      .catch((err) => setErrorPaso(err?.response?.data?.error || "No se pudo cargar el detalle de este paso."))
      .finally(() => setCargandoPaso(false));
  };

  const imprimirNotaEntrega = async () => {
    // Ventana en blanco abierta ya, en el mismo gesto de click — si se abre después de un
    // await, el navegador la bloquea como popup (mismo patrón que Facturación/ConcluirOT).
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }
    pdfWindow.opener = null;
    pdfWindow.document.title = "Cargando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Cargando PDF...</div>';

    setImprimiendo(true);
    try {
      const { data } = await axios.get(`/api/ot-solicitudes/${id}/nota-entrega.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      pdfWindow.close();
      alert(err?.response?.data?.error || "No se pudo generar la Nota de Entrega.");
    } finally {
      setImprimiendo(false);
    }
  };

  // Igual que imprimirNotaEntrega, pero para la Nota de Devolución (tipo_ot=Devolucion,
  // ver otNotaDevolucionPdfController.js) — ya existe desde que se finalizó la Fase 1
  // (OT en "Asignado" o más adelante), no hace falta esperar a que se concluya.
  const imprimirNotaDevolucion = async () => {
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }
    pdfWindow.opener = null;
    pdfWindow.document.title = "Cargando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Cargando PDF...</div>';

    setImprimiendo(true);
    try {
      const { data } = await axios.get(`/api/ot-solicitudes/${id}/nota-devolucion.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      pdfWindow.close();
      alert(err?.response?.data?.error || "No se pudo generar la Nota de Devolución.");
    } finally {
      setImprimiendo(false);
    }
  };

  const imprimirNotaMantenimientoTraspaso = async () => {
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }
    pdfWindow.opener = null;
    pdfWindow.document.title = "Cargando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Cargando PDF...</div>';

    setImprimiendo(true);
    try {
      const { data } = await axios.get(`/api/ot-solicitudes/${id}/nota-mantenimiento-traspaso.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      pdfWindow.close();
      alert(err?.response?.data?.error || "No se pudo generar la Nota de Traspaso.");
    } finally {
      setImprimiendo(false);
    }
  };

  // Igual que imprimirNotaMantenimientoTraspaso, pero para la Nota de Mantenimiento
  // normal (tipo_ot=2, ver otNotaMantenimientoPdfController.js) — antes esta OT caía en
  // el botón genérico "Imprimir Nota de Entrega" (nota-entrega.pdf, pensada para
  // Adición), que mostraba el título/columnas equivocados ("... ADICIONADAS") para un
  // Mantenimiento.
  const imprimirNotaMantenimiento = async () => {
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }
    pdfWindow.opener = null;
    pdfWindow.document.title = "Cargando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Cargando PDF...</div>';

    setImprimiendo(true);
    try {
      const { data } = await axios.get(`/api/ot-solicitudes/${id}/nota-mantenimiento.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      pdfWindow.close();
      alert(err?.response?.data?.error || "No se pudo generar la Nota de Mantenimiento.");
    } finally {
      setImprimiendo(false);
    }
  };

  // Nota de Devolución de Chips (tipo_ot=29, ver otNotaDevolucionChipsPdfController.js): la genérica "Nota de Entrega" no sabe de chips devueltos.
  const imprimirNotaDevolucionChips = async () => {
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }
    pdfWindow.opener = null;
    pdfWindow.document.title = "Cargando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Cargando PDF...</div>';

    setImprimiendo(true);
    try {
      const { data } = await axios.get(`/api/ot-solicitudes/${id}/nota-devolucion-chips.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      pdfWindow.close();
      alert(err?.response?.data?.error || "No se pudo generar la Nota de Devolución de Chips.");
    } finally {
      setImprimiendo(false);
    }
  };

  // Igual que imprimirNotaMantenimiento, pero para Remplazo Equipos Vendidos Activos
  // (tipo_ot=16, ver otNotaRemplazoVendidosActivosPdfController.js) — la genérica "Nota
  // de Entrega" no muestra precio_venta ni el estado 'Vendido Activo'.
  const imprimirNotaRemplazoVendidosActivos = async () => {
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }
    pdfWindow.opener = null;
    pdfWindow.document.title = "Cargando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Cargando PDF...</div>';

    setImprimiendo(true);
    try {
      const { data } = await axios.get(`/api/ot-solicitudes/${id}/nota-remplazo-vendidos-activos.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      pdfWindow.close();
      alert(err?.response?.data?.error || "No se pudo generar la Nota de Reemplazo.");
    } finally {
      setImprimiendo(false);
    }
  };

  const handleConfirmarAvanzar = () => {
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setAvanzando(true);
    setAvanzarError("");
    axios.patch(`/api/ot-solicitudes/${id}/avanzar`, { observacion: avanzarObs })
      .then((res) => {
        setOrden(res.data);
        setAvanzarOpen(false);
        setAvanzarObs("");
      })
      .catch((err) => {
        setAvanzarError(err?.response?.data?.error || "No se pudo avanzar el estado.");
      })
      .finally(() => {
        enviandoEstadoRef.current = false;
        setAvanzando(false);
      });
  };

  const abrirEditarEstado = () => {
    setNuevoEstado(Number(orden?.estado) || 0);
    setErrorEditarEstado("");
    setEditarEstadoOpen(true);
  };

  const handleConfirmarEditarEstado = () => {
    setGuardandoEstado(true);
    setErrorEditarEstado("");
    axios.patch(`/api/ot-solicitudes/${id}/estado-admin`, { estado: nuevoEstado })
      .then((res) => {
        setOrden(res.data);
        setEditarEstadoOpen(false);
      })
      .catch((err) => {
        setErrorEditarEstado(err?.response?.data?.error || "No se pudo actualizar el estado.");
      })
      .finally(() => setGuardandoEstado(false));
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }
  if (!orden) {
    return (
      <Layout>
        <Typography color="error">Orden no encontrada</Typography>
      </Layout>
    );
  }

  const estadoNum = Number(orden.estado);
  const esTKR = orden.tipo_servicio === "TKR";
  const esPOC = orden.tipo_servicio === "POC";
  const esDevolucion = Number(orden.tipo_ot) === TIPO_OT_DEVOLUCION;
  const esDesactivarVendidos = Number(orden.tipo_ot) === TIPO_OT_DESACTIVAR_VENDIDOS;
  const esVentaOrden = Number(orden.tipo_ot) === TIPO_OT_VENTA;
  const esMantenimientoTraspaso = Number(orden.tipo_ot) === TIPO_OT_MANTENIMIENTO_TRASPASO;
  const esMantenimiento = [TIPO_OT_MANTENIMIENTO, TIPO_OT_MANTENIMIENTO_VENDIDOS].includes(Number(orden.tipo_ot));
  const esRemplazoVendidosActivos = [TIPO_OT_REMPLAZO_VENDIDOS_ACTIVOS, 19].includes(Number(orden.tipo_ot)); // 19 = Remplazo Equipos Vendidos Inactivos (mismo flujo y nota)
  // El paso de Cochabamba termina en "Enviar a Mantenimiento" (estado 3); un usuario de
  // La Paz sí debe poder seguir viendo el paso como disponible para continuar el
  // diagnóstico — mismo criterio que escritorio_detalle.php (bloqueaba solo cuando la
  // sucursal del usuario que ve la OT era Cochabamba).
  const bloqueadoPorTraspasoCochabamba = esMantenimientoTraspaso && user?.sucursal === "Cochabamba";
  const puedeAdicionar =
    !esDevolucion && !SERVICIOS_SIN_ADICION.includes(orden.tipo_servicio) && estadoNum >= 1 && estadoNum <= 2;
  const puedeDevolver = esDevolucion && estadoNum >= 1 && estadoNum <= 2;
  // Desactivar Equipos Vendidos (13) SÍ incluye AVL/Scada, a diferencia de Adición —
  // se declara aparte para no depender de que `puedeAdicionar` cubra ese caso (que lo
  // excluye vía SERVICIOS_SIN_ADICION).
  const puedeDesactivarVendidos = esDesactivarVendidos && estadoNum >= 1 && estadoNum <= 2;
  const puedeAdicionChips = Number(orden.tipo_ot) === TIPO_OT_ADICION_CHIPS && estadoNum >= 1 && estadoNum <= 2;
  const puedeDevolverChips = Number(orden.tipo_ot) === TIPO_OT_DEVOLUCION_CHIPS && estadoNum >= 1 && estadoNum <= 2;
  // Venta de Equipo (11) también incluye AVL/Scada (VenderEquipoOT.jsx ya rama a
  // Módem/Chip) — se declara aparte por el mismo motivo que Desactivar Vendidos: sin
  // esto, `puedeAdicionar` (que SÍ excluye AVL/Scada vía SERVICIOS_SIN_ADICION) es el
  // único flag de esta página que cubre estados 1-2, y una Venta AVL se quedaba sin
  // pantalla propia — mostraba el diálogo genérico de "Avanzar a ..." en vez de
  // VenderEquipoYAccesoriosOT. Se extiende hasta 3 (no solo 1-2) porque en "Asignado"
  // Venta tiene su propio paso (Ingreso de Placa / Activar Venta, ver puedeIngresarPlaca/
  // puedeActivarVenta en OrdenTrabajoPaso.jsx) que tampoco caía en `puedeEmparejar`
  // (esa acepta solo esTKR||esPOC) para un servicio AVL.
  const puedeVender = esVentaOrden && estadoNum >= 1 && estadoNum <= 3;
  // Venta de Equipos Activos (14): mismo motivo que puedeVender — sin este flag, AVL
  // se quedaba sin pantalla propia (VenderEquipoActivoOT.jsx ya recibe esAVL). No hay
  // estado intermedio "Asignado" propio acá (a diferencia de Venta), así que alcanza
  // con 1-2.
  const puedeVenderActivo = Number(orden.tipo_ot) === TIPO_OT_VENTA_ACTIVOS && estadoNum >= 1 && estadoNum <= 2;
  // Solo TKR y POC pasan por "Asignado" (el resto salta directo a "Procesando"
  // desde Finalizar Adición), así que Emparejar solo aplica ahí — y nunca a Devolución
  // (ese mismo estado significa Fase 2 pendiente ahí, ver puedeClasificarDevolucion).
  const puedeEmparejar = !esDevolucion && !bloqueadoPorTraspasoCochabamba && (esTKR || esPOC) && estadoNum === 3;
  // Entrega de accesorios aplica a cualquier servicio que llegue a "Procesando" —
  // Devolución no usa este estado para nada propio, se excluye igual.
  const puedeEntregar = !esDevolucion && !bloqueadoPorTraspasoCochabamba && estadoNum === 4;
  // "Asignado" (3) es el estado normal de entrada a la Fase 2 (ver
  // finalizarSeleccionDevolucionTx); 4-5 quedan cubiertos por si una OT de Devolución
  // vieja/mal-empujada quedó en "Procesando"/"Entregado" por la pantalla legacy.
  const puedeClasificarDevolucion = esDevolucion && estadoNum >= 3 && estadoNum <= 5;
  // Concluir (precios + facturación) aplica a cualquier servicio en "Entregado".
  // Pasos propios de estado 3 que NO dependen de que el servicio sea TKR/POC (puedeEmparejar sí): el diagnóstico de Mantenimiento
  // y el paso Asignar de los Reemplazos. Sin esto, una OT de otros servicios (p. ej. "Ventas Varias") en "Asignado" caía en el
  // diálogo genérico "Avanzar a Procesando" y no había forma de volver a la pantalla de trabajo.
  const puedeDiagnosticar = (esMantenimiento || esMantenimientoTraspaso) && !bloqueadoPorTraspasoCochabamba && estadoNum === 3;
  const puedeAsignarReemplazo = (Number(orden.tipo_ot) === TIPO_OT_REEMPLAZO || esRemplazoVendidosActivos) && estadoNum === 3;
  const puedeConcluir = estadoNum === 5;
  const puedeFinalizar = estadoNum === 6;
  const tienePantallaDeTrabajo =
    puedeAdicionar || puedeDevolver || puedeDesactivarVendidos || puedeAdicionChips || puedeDevolverChips || puedeVender || puedeVenderActivo || puedeEmparejar || puedeEntregar ||
    puedeClasificarDevolucion || puedeDiagnosticar || puedeAsignarReemplazo || puedeConcluir || puedeFinalizar;
  // La Nota de Entrega recién tiene observación de conclusión y fecha de culminación
  // reales una vez que la OT pasó de "Concluido" — antes de eso queda incompleta. No
  // aplica a Devolución (esa nota es de Adición, quedaría vacía/con datos que no son) ni
  // a Mantenimiento (tiene su propia Nota, ver puedeImprimirNotaMantenimiento).
  const esDevolucionChipsOrden = Number(orden.tipo_ot) === TIPO_OT_DEVOLUCION_CHIPS;
  const puedeImprimirNota = !esDevolucion && !esMantenimiento && !esRemplazoVendidosActivos && !esDevolucionChipsOrden && estadoNum >= 6;
  // Devolución de Chips tiene su propia Nota (la genérica no muestra chips devueltos).
  const puedeImprimirNotaDevolucionChips = esDevolucionChipsOrden && estadoNum >= 6;
  // Igual que puedeImprimirNota pero para la Nota de Mantenimiento propia (no la
  // genérica de Entrega/Adición).
  const puedeImprimirNotaMantenimiento = esMantenimiento && estadoNum >= 6;
  // Igual que puedeImprimirNotaMantenimiento pero para Remplazo Equipos Vendidos Activos.
  const puedeImprimirNotaRemplazoVendidosActivos = esRemplazoVendidosActivos && estadoNum >= 6;
  // La Nota de Devolución ya tiene datos reales desde que se finalizó la Fase 1 (la OT
  // llega a "Asignado"(3) con la observación general y el equipo/accesorios ya
  // guardados) — no hace falta esperar a que se concluya la OT para poder reimprimirla.
  const puedeImprimirNotaDevolucion = esDevolucion && estadoNum >= 3;
  // Mantenimiento Traspaso queda sin pasos apenas se envía el equipo (estado 3,
  // "Asignado") y nunca llega a Concluido/Finalizado — la Nota ya tiene datos reales
  // desde ese mismo momento, mismo criterio que puedeImprimirNotaDevolucion.
  const puedeImprimirNotaMantenimientoTraspaso = esMantenimientoTraspaso && estadoNum >= 3;

  const handleClicSiguiente = () => {
    if (tienePantallaDeTrabajo) {
      navigate(`/orden-de-trabajo/ot/${id}/paso`);
    } else {
      setAvanzarOpen(true);
    }
  };

  return (
    <Layout>
      {!embedded && (
        <PageHeader
          title={`Orden de Trabajo #${orden.id}`}
          subtitle={`${orden.razon_social}${orden.descripcion ? ` • ${orden.descripcion}` : ""} • Cod. Servicio: ${orden.id_servicio_cliente} • ${orden.tipo_servicio}`}
        />
      )}

      <Box sx={{ maxWidth: 760, mx: "auto", p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <Typography variant="h6">
              Descripción de la O.T. <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>{orden.id}</Box>
            </Typography>
            <Stack direction="row" spacing={1}>
              {puedeImprimirNota && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={imprimiendo ? <CircularProgress size={14} /> : <PrintIcon fontSize="small" />}
                  onClick={imprimirNotaEntrega}
                  disabled={imprimiendo}
                >
                  Imprimir Nota de Entrega
                </Button>
              )}
              {puedeImprimirNotaDevolucionChips && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={imprimiendo ? <CircularProgress size={14} /> : <PrintIcon fontSize="small" />}
                  onClick={imprimirNotaDevolucionChips}
                  disabled={imprimiendo}
                >
                  Imprimir Nota de Devolución de Chips
                </Button>
              )}
              {puedeImprimirNotaMantenimiento && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={imprimiendo ? <CircularProgress size={14} /> : <PrintIcon fontSize="small" />}
                  onClick={imprimirNotaMantenimiento}
                  disabled={imprimiendo}
                >
                  Imprimir Nota de Mantenimiento
                </Button>
              )}
              {puedeImprimirNotaRemplazoVendidosActivos && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={imprimiendo ? <CircularProgress size={14} /> : <PrintIcon fontSize="small" />}
                  onClick={imprimirNotaRemplazoVendidosActivos}
                  disabled={imprimiendo}
                >
                  Imprimir Nota de Reemplazo
                </Button>
              )}
              {puedeImprimirNotaDevolucion && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={imprimiendo ? <CircularProgress size={14} /> : <PrintIcon fontSize="small" />}
                  onClick={imprimirNotaDevolucion}
                  disabled={imprimiendo}
                >
                  Imprimir Nota de Devolución
                </Button>
              )}
              {puedeImprimirNotaMantenimientoTraspaso && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={imprimiendo ? <CircularProgress size={14} /> : <PrintIcon fontSize="small" />}
                  onClick={imprimirNotaMantenimientoTraspaso}
                  disabled={imprimiendo}
                >
                  Imprimir Nota de Traspaso
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<HistoryIcon fontSize="small" />}
                onClick={abrirHistorial}
              >
                Ver Historial
              </Button>
              {user?.role === "admin" && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={abrirEditarEstado}
                >
                  Editar Estado
                </Button>
              )}
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack spacing={1}>
                <InfoRow icon={<CheckCircleOutlineIcon fontSize="small" />} label="Estado Actual OT" value={ESTADOS[estadoNum] || "—"} />
                <InfoRow icon={<LabelOutlinedIcon fontSize="small" />} label="Tipo OT" value={orden.nombre_ot_tipo} />
                <InfoRow icon={<EventOutlinedIcon fontSize="small" />} label="Fecha de Emisión" value={fmt(orden.fecha_registro)} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack spacing={1}>
                <InfoRow icon={<EventOutlinedIcon fontSize="small" />} label="Fecha Proceso" value={fmt(orden.fecha_proceso)} />
                <InfoRow icon={<EventOutlinedIcon fontSize="small" />} label="Fecha Culminación" value={fmt(orden.fecha_culminacion)} />
                <InfoRow icon={<ReceiptLongOutlinedIcon fontSize="small" />} label="Num. Recibo" value={fmt(orden.num_recibo)} />
              </Stack>
            </Grid>
          </Grid>

          {orden.observacion && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
              <b>Observación:</b> {orden.observacion}
            </Alert>
          )}
          {orden.nom_tecnico && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
              <b>Técnico(s):</b> {orden.nom_tecnico}
            </Alert>
          )}
        </Paper>

        <Box sx={{ mb: 1.5 }}>
          <EstadoBar
            estado={estadoNum}
            onClicSiguiente={handleClicSiguiente}
            onClicCompletado={abrirDetallePaso}
            // Antes de "Asignado" (3) el paso de Cochabamba (Enviar a Mantenimiento)
            // sigue disponible normalmente — el bloqueo es a partir de ahí, no desde
            // la creación de la OT.
            bloqueado={bloqueadoPorTraspasoCochabamba && estadoNum >= 3}
          />
        </Box>

        {pasoDialog && (
          <Paper variant="outlined" sx={{ mb: 1.5, overflow: "hidden" }}>
            <Box
              sx={{
                bgcolor: "#00796b",
                color: "#fff",
                py: 1,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle1" fontWeight={800}>{pasoDialog.label}</Typography>
              <IconButton size="small" onClick={() => setPasoDialog(null)} sx={{ color: "#fff" }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>
              {errorPaso && <Alert severity="error" sx={{ mb: 2 }}>{errorPaso}</Alert>}
              {cargandoPaso ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : !pasoFilas.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  No hay información registrada para este paso.
                </Typography>
              ) : (
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        {pasoColumnas.map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.grey[800] : "#e0f2f1"),
                            }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pasoFilas.map((fila, idx) => (
                        <TableRow key={idx}>
                          {pasoColumnas.map((col) => {
                            const esSerieAsignable =
                              col === "Serie" &&
                              (pasoDialog?.label === "Asignado" || pasoDialog?.label === "Procesando") &&
                              fila.Serie;
                            return (
                              <TableCell key={col} sx={{ whiteSpace: "pre-line" }}>
                                {esSerieAsignable ? (
                                  <Box
                                    component="span"
                                    onClick={() => setEquipoDialog({ tipo: familiaDeTipoLabel(fila.Tipo), identificador: fila.Serie })}
                                    sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                                  >
                                    {fmt(fila[col])}
                                  </Box>
                                ) : (
                                  fmt(fila[col])
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      <Dialog open={avanzarOpen} onClose={avanzando ? undefined : () => setAvanzarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Avanzar a "{ESTADOS[estadoNum + 1]}"
        </DialogTitle>
        <DialogContent>
          {avanzarError && <Alert severity="error" sx={{ mb: 2 }}>{avanzarError}</Alert>}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={OBSERVACION_LABEL[estadoNum + 1] || "Observación (opcional)"}
            value={avanzarObs}
            onChange={(e) => setAvanzarObs(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvanzarOpen(false)} disabled={avanzando}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarAvanzar} disabled={avanzando}>
            {avanzando ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editarEstadoOpen} onClose={guardandoEstado ? undefined : () => setEditarEstadoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar Estado — OT #{orden.id}</DialogTitle>
        <DialogContent>
          {errorEditarEstado && <Alert severity="error" sx={{ mb: 2 }}>{errorEditarEstado}</Alert>}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="editar-estado-label">Estado</InputLabel>
            <Select
              labelId="editar-estado-label"
              label="Estado"
              value={nuevoEstado}
              onChange={(e) => setNuevoEstado(Number(e.target.value))}
            >
              {ESTADOS.map((label, idx) => (
                <MenuItem key={label} value={idx}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditarEstadoOpen(false)} disabled={guardandoEstado}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmarEditarEstado}
            disabled={guardandoEstado || nuevoEstado === Number(orden?.estado)}
          >
            {guardandoEstado ? <CircularProgress size={20} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historialOpen} onClose={() => setHistorialOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Historial de la OT {orden.id}
          <IconButton size="small" onClick={() => setHistorialOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {errorHistorial && <Alert severity="error" sx={{ mb: 2 }}>{errorHistorial}</Alert>}
          {cargandoHistorial ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : !historial.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No hay eventos registrados para esta OT.
            </Typography>
          ) : (
            <Timeline
              sx={{
                p: 0,
                m: 0,
                "& .MuiTimelineOppositeContent-root": { flex: 0.32, py: 1.5 },
              }}
            >
              {historial.map((h, idx) => (
                <TimelineItem key={h.id}>
                  <TimelineOppositeContent color="text.secondary" sx={{ fontSize: 11 }}>
                    {fmt(h.fecha && String(h.fecha).slice(0, 10))}
                    <br />
                    {h.hora}
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    {idx === historial.length - 1 ? (
                      <TimelineDot color={colorEventoHistorial(h.evento)} />
                    ) : (
                      <TimelineDot variant="outlined" color="grey" />
                    )}
                    {idx < historial.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {h.evento}
                    </Typography>
                    {String(h.detalle || "")
                      .split("%%%")
                      .slice(1)
                      .filter(Boolean)
                      .map((linea, i) => (
                        <Typography key={i} variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
                          {linea}
                        </Typography>
                      ))}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {h.usu ? `Usuario: ${h.usu}` : ""}{h.sucursal ? ` · ${h.sucursal}` : ""}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistorialOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />

    </Layout>
  );
};

export default OrdenTrabajoDetalle;
